import {
  useCostCentersQuery,
  useTeamsQuery,
  useCustomFieldsQuery,
} from 'src/core/modules/budgets/apis';

import { useSpendTypes } from './useSpendTypeOptions';
import { type Dimension } from '../approvalWorkflow';

export const useDimensionValues = () => {
  const spendTypes = useSpendTypes();
  const costCentersQuery = useCostCentersQuery();
  const teamsQuery = useTeamsQuery();
  const customFieldsQuery = useCustomFieldsQuery();

  const getCostCenterValues = () =>
    costCentersQuery.status === 'success'
      ? (costCentersQuery.data?.map((cc) => cc.id) ?? [])
      : [];

  const getTeamValues = () =>
    teamsQuery.status === 'success'
      ? (teamsQuery.data?.map((team) => team.id) ?? [])
      : [];

  const getCustomFieldValues = (customFieldId?: string) => {
    if (!customFieldId) {
      return [];
    }
    return customFieldsQuery.status === 'success'
      ? (customFieldsQuery.data?.customFields
          .find((customField) => customField.id === customFieldId)
          ?.custom_fields_values?.map((value) => value.id) ?? [])
      : [];
  };

  const getExpenseCategoryValues = () =>
    customFieldsQuery.status === 'success'
      ? (customFieldsQuery.data.expenseCategoryField?.custom_fields_values?.map(
          (ec) => ec.id,
        ) ?? [])
      : [];

  const getDimensionValues = (
    dimensionType: Dimension['type'],
    customFieldId?: string,
  ) => {
    switch (dimensionType) {
      case 'spendType':
        return spendTypes;
      case 'costCenter':
        return getCostCenterValues();
      case 'team':
        return getTeamValues();
      case 'customField':
        return getCustomFieldValues(customFieldId);
      case 'expenseCategory':
        return getExpenseCategoryValues();
      default:
        return [];
    }
  };

  const hasAllValuesSelected = (
    dimensionType: Dimension['type'],
    selectedValues: string[],
  ) => {
    const allPossibleValues = getDimensionValues(dimensionType);
    return allPossibleValues.length === selectedValues.length;
  };

  return {
    getDimensionValues,
    hasAllValuesSelected,
  };
};
